import React from 'react';
import { motion } from 'framer-motion';

const CoreFeatures: React.FC = () => {
  return (
    <section className="py-24 bg-black relative overflow-hidden">
      <div className="container mx-auto px-6">
        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-2">
            Core <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">features</span>
          </h2>
        </motion.div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Knowledge Hub */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="bg-gray-900/50 border border-gray-800 rounded-xl p-8 relative overflow-hidden"
          >
            {/* Chat Interface Mockup */}
            <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 mb-6">
              <div className="flex items-start space-x-3 mb-4">
                <div className="w-8 h-8 bg-gray-600 rounded-full flex items-center justify-center">
                  <span className="text-xs text-white">👤</span>
                </div>
                <div className="flex-1">
                  <div className="text-xs text-gray-400 mb-1">You 8:15 AM</div>
                  <div className="text-sm text-gray-300">We have a failed valve (3,000 PSI, 2-inch). Compare with our datasheet and suggest a replacement.</div>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <span className="text-xs text-white font-bold">AI</span>
                </div>
                <div className="flex-1">
                  <div className="text-xs text-gray-400 mb-1">AI Assistant 8:15 AM</div>
                  <div className="text-sm text-gray-300">Valve Y is the best fit—3,000 PSI, 2-inch, same alloy, and readily available.</div>
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between text-gray-500">
                <div className="flex items-center space-x-1">
                  <span className="text-xs">+</span>
                  <span className="text-xs">Message AI Assistant...</span>
                </div>
                <div className="w-6 h-6 bg-gray-600 rounded flex items-center justify-center">
                  <span className="text-xs">↑</span>
                </div>
              </div>
            </div>
            <h3 className="text-2xl font-bold text-white mb-3">Knowledge Hub</h3>
            <p className="text-gray-400 text-sm leading-relaxed">
              We develop intelligent AI assistant acting as a single point of reference, pulling together data from multiple sources, so teams can quickly find insights.
            </p>
          </motion.div>

          {/* Customized Visualization */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="bg-gray-900/50 border border-gray-800 rounded-xl p-8 relative overflow-hidden"
          >
            {/* Dashboard Mockup */}
            <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 mb-6 relative">
              {/* Starfield background */}
              <div className="absolute inset-0 rounded-lg overflow-hidden">
                {Array.from({ length: 20 }).map((_, i) => (
                  <div
                    key={i}
                    className="absolute w-1 h-1 bg-white rounded-full animate-pulse"
                    style={{
                      left: `${Math.random() * 100}%`,
                      top: `${Math.random() * 100}%`,
                      animationDelay: `${Math.random() * 3}s`,
                      opacity: Math.random() * 0.8 + 0.2
                    }}
                  />
                ))}
              </div>
              <div className="relative z-10 text-center py-8">
                <div className="text-gray-400 text-sm mb-2">Generating report...</div>
              </div>
              <div className="mt-4 flex items-center justify-between bg-gray-700/50 rounded p-2">
                <span className="text-xs text-gray-400">Generate a report for...</span>
                <button className="bg-blue-600 text-white text-xs px-3 py-1 rounded">Generate</button>
              </div>
            </div>
            <h3 className="text-2xl font-bold text-white mb-3">Customized Visualization</h3>
            <p className="text-gray-400 text-sm leading-relaxed">
              We generate clear, real-time dashboards and graphs, turning raw data into actionable intelligence without manual crunching.
            </p>
          </motion.div>
        </div>

        {/* Bottom Three Features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Agentic Workflow */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="bg-gray-900/50 border border-gray-800 rounded-xl p-8"
          >
            {/* Workflow visualization */}
            <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 mb-6 relative h-32">
              <div className="flex items-center justify-center h-full">
                <div className="relative">
                  {/* Central circle */}
                  <div className="w-16 h-16 bg-blue-600/20 border-2 border-blue-400 rounded-full flex items-center justify-center">
                    <span className="text-blue-400 font-bold text-sm">100+</span>
                  </div>
                  <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 text-xs text-blue-400">Automations</div>
                  
                  {/* Surrounding icons */}
                  <div className="absolute -top-2 -left-2 w-6 h-6 bg-gray-700 rounded border border-gray-600"></div>
                  <div className="absolute -top-2 -right-2 w-6 h-6 bg-blue-600 rounded border border-blue-400"></div>
                  <div className="absolute -bottom-2 -left-2 w-6 h-6 bg-gray-700 rounded border border-gray-600"></div>
                  <div className="absolute -bottom-2 -right-2 w-6 h-6 bg-gray-700 rounded border border-gray-600"></div>
                  <div className="absolute top-1/2 -left-6 transform -translate-y-1/2 w-6 h-6 bg-gray-700 rounded border border-gray-600"></div>
                  <div className="absolute top-1/2 -right-6 transform -translate-y-1/2 w-6 h-6 bg-gray-700 rounded border border-gray-600"></div>
                </div>
              </div>
            </div>
            <h3 className="text-2xl font-bold text-white mb-3">Agentic Workflow</h3>
            <p className="text-gray-400 text-sm leading-relaxed">
              We take on repetitive or manual tasks (e.g., data entry, scheduling, form filling), freeing up human teams for higher-value work.
            </p>
          </motion.div>

          {/* Data Engineering */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="bg-gray-900/50 border border-gray-800 rounded-xl p-8"
          >
            {/* Data processing visualization */}
            <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 mb-6 relative h-32">
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="text-white text-sm mb-2">Can you help clean up these unstructured data into the same format?</div>
                  <div className="text-blue-400 text-xs">Use historical data to find a unified format...</div>
                  <div className="mt-3 flex justify-center">
                    <div className="bg-blue-600 text-white text-xs px-2 py-1 rounded">Yes</div>
                  </div>
                </div>
              </div>
            </div>
            <h3 className="text-2xl font-bold text-white mb-3">Data Engineering</h3>
            <p className="text-gray-400 text-sm leading-relaxed">
              We collect and organize messy information, efficiently transform raw, unstructured information into structured, reliable datasets that can be properly analyzed to extract meaningful insights.
            </p>
          </motion.div>

          {/* Continuous Learning */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
            className="bg-gray-900/50 border border-gray-800 rounded-xl p-8"
          >
            {/* Learning visualization */}
            <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 mb-6 relative h-32">
              <div className="flex items-center justify-center h-full">
                <div className="relative w-full">
                  {/* Efficiency line */}
                  <div className="absolute top-4 right-4 text-xs text-white bg-gray-700 px-2 py-1 rounded">
                    Efficiency +10.3%
                  </div>
                  {/* Cost line */}
                  <div className="absolute bottom-8 left-4 text-xs text-white bg-gray-700 px-2 py-1 rounded">
                    Cost -6.7%
                  </div>
                  {/* Chart lines */}
                  <svg className="w-full h-full" viewBox="0 0 200 80">
                    <path 
                      d="M20,60 Q60,40 100,30 T180,20" 
                      stroke="#3b82f6" 
                      strokeWidth="2" 
                      fill="none"
                    />
                    <path 
                      d="M20,40 Q60,50 100,45 T180,35" 
                      stroke="#06b6d4" 
                      strokeWidth="2" 
                      fill="none"
                    />
                    <circle cx="180" cy="20" r="3" fill="#3b82f6" />
                    <circle cx="180" cy="35" r="3" fill="#06b6d4" />
                  </svg>
                </div>
              </div>
            </div>
            <h3 className="text-2xl font-bold text-white mb-3">Continuous Learning</h3>
            <p className="text-gray-400 text-sm leading-relaxed">
              We learn from daily operations and historical data to improve recommendations, making it more valuable over time.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default CoreFeatures;